// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

// CareRx Lab Tracker reads — TanStack Query hooks (E-02). Review 2026-10-02: the page loaded with
// `.then(setState).catch(() => setState(null))`, so a 403 (platform actor without break-glass) or a
// 404 (another pharmacy's therapy) left an endless "Loading…" or an empty tracker with no message.
// The hooks expose the error so the page can say what happened.

import { useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ApiError, bulkLabTrackerLabs, getLabTrackerDetail, listLabProviders, listLabTracker, redrawLabTrackerLab,
  setLabTrackerDates, setLabTrackerProvider, setLabTrackerProviderOutreach, updateLabTrackerLab,
} from '../../api/client'
import { friendlyError } from '../../api/errors'
import { useToast } from '../../components/Toast'

export const labTrackerKeys = {
  list: ['carerx', 'lab-tracker'] as const,
  detail: (therapyId: string) => ['carerx', 'lab-tracker', therapyId] as const,
  providers: ['carerx', 'lab-providers'] as const,
}

export function useLabTracker() {
  return useQuery({ queryKey: labTrackerKeys.list, queryFn: listLabTracker })
}

export function useLabTrackerDetail(therapyId: string) {
  return useQuery({ queryKey: labTrackerKeys.detail(therapyId), queryFn: () => getLabTrackerDetail(therapyId) })
}

export function useLabProviders() {
  return useQuery({ queryKey: labTrackerKeys.providers, queryFn: () => listLabProviders(), staleTime: 5 * 60_000 })
}

type Dates = Parameters<typeof setLabTrackerDates>[1]
type Outreach = Parameters<typeof setLabTrackerProviderOutreach>[1]
type LabEdit = Parameters<typeof updateLabTrackerLab>[1]

/**
 * Every Lab Tracker write (E-02; review 2026-10-02, S8/W2-D). On success the tracker list and the
 * open therapy refresh (one key prefix covers both). A failure is SAID: a 4xx is toasted here (the
 * API client already toasts network errors and 5xx), and each writer returns the promise so a
 * DateInput puts the saved date back instead of showing one that never stored.
 */
export function useLabTrackerWrites() {
  const qc = useQueryClient()
  const toast = useToast()
  const { mutateAsync } = useMutation({
    mutationFn: (call: () => Promise<unknown>) => call(),
    onSuccess: () => qc.invalidateQueries({ queryKey: labTrackerKeys.list }),
    onError: (e: unknown) => {
      if (e instanceof ApiError && e.status < 500) toast.show(friendlyError(e, 'The change was not saved.'), 'error')
    },
  })
  return useMemo(() => {
    const run = (call: () => Promise<unknown>) => mutateAsync(call)
    return {
      setProvider: (therapyId: string, providerId: string | null) => run(() => setLabTrackerProvider(therapyId, providerId)),
      setDates: (therapyId: string, body: Dates) => run(() => setLabTrackerDates(therapyId, body)),
      setProviderOutreach: (therapyId: string, body: Outreach) => run(() => setLabTrackerProviderOutreach(therapyId, body)),
      updateLab: (labId: string, body: LabEdit) => run(() => updateLabTrackerLab(labId, body)),
      bulk: (body: Parameters<typeof bulkLabTrackerLabs>[0]) => run(() => bulkLabTrackerLabs(body)),
      redraw: (labId: string, body: { reason: string; redraw_on: string }) => run(() => redrawLabTrackerLab(labId, body)),
      /** For controls that do not need the outcome: the failure was already reported. */
      quietly: (p: Promise<unknown>) => { p.catch(() => undefined) },
    }
  }, [mutateAsync])
}
